import { useEffect, useMemo, useRef, useState } from "react";
import {
  Briefcase,
  DollarSign,
  Clock,
  AlertCircle,
  CheckCircle2,
  Plus,
  ArrowRight,
  MessageSquare,
  MoreVertical,
  Archive,
  ArchiveRestore,
  Trash2,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import { supabase } from "../lib/supabase.js";
import FeedbackPanel from "./FeedbackPanel.jsx";

const FEE_TYPE_LABELS = {
  fixed: "Fixed",
  hourly: "Hourly",
  contingent: "Contingent",
  hybrid: "Hybrid",
  percent_of_credit: "% of credit",
  value_based: "Value-based",
};

const STATUS_STYLES = {
  not_started: { label: "Not started", bg: "bg-stone-100", text: "text-stone-600" },
  proposal_sent: { label: "Proposal sent", bg: "bg-amber-50", text: "text-amber-700" },
  letter_drafted: { label: "Letter drafted", bg: "bg-amber-50", text: "text-amber-700" },
  letter_signed: { label: "Letter signed", bg: "bg-sky-50", text: "text-sky-700" },
  in_progress: { label: "In progress", bg: "bg-sky-50", text: "text-sky-700" },
  review: { label: "In review", bg: "bg-violet-50", text: "text-violet-700" },
  delivered: { label: "Delivered", bg: "bg-emerald-50", text: "text-emerald-700" },
  stalled: { label: "Stalled", bg: "bg-rose-50", text: "text-rose-700" },
};

function statusBadge(status) {
  const meta = STATUS_STYLES[status] || {
    label: status?.replace(/_/g, " ") || "Unknown",
    bg: "bg-stone-100",
    text: "text-stone-600",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium sans ${meta.bg} ${meta.text}`}
    >
      {meta.label}
    </span>
  );
}

function fmtMoney(n) {
  const v = parseFloat(n) || 0;
  return v.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function fmtRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function EngagementsHome({ profile, onOpenEngagement }) {
  const [engagements, setEngagements] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackCounts, setFeedbackCounts] = useState({ open: 0, total: 0 });
  const [filterView, setFilterView] = useState("active"); // active | completed | all
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [sortField, setSortField] = useState("updated_at");
  const [sortDirection, setSortDirection] = useState("desc");

  const refresh = async () => {
    setError(null);
    const [{ data: engs, error: e1 }, { data: ps, error: e2 }] = await Promise.all([
      supabase
        .from("engagements")
        .select(
          "id, name, client_name, tax_year, entity_type, status, fee_type, fee_amount, total_hours, percent_complete, pending_items_count, assigned_to_id, updated_at, archived",
        )
        .order("updated_at", { ascending: false }),
      supabase.from("user_profiles").select("id, email, display_name"),
    ]);
    if (e1 || e2) {
      setError((e1 || e2).message);
      setLoading(false);
      return;
    }
    setEngagements(engs || []);
    const byId = {};
    for (const p of ps || []) byId[p.id] = p;
    setProfiles(byId);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    const channel = supabase
      .channel("engagements-list")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "engagements" },
        () => refresh(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Track feedback counts for the header badge
  useEffect(() => {
    const refreshCounts = async () => {
      const { data } = await supabase
        .from("feedback")
        .select("content");
      const total = data?.length || 0;
      const open = (data || []).filter(
        (r) => (r.content?.status || "open") === "open",
      ).length;
      setFeedbackCounts({ open, total });
    };
    refreshCounts();
    const channel = supabase
      .channel("feedback-counts")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "feedback" },
        () => refreshCounts(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const { activeTotals, completedTotals } = useMemo(() => {
    const make = () => ({ count: 0, fees: 0, hours: 0, pending: 0 });
    const active = make();
    const completed = make();
    for (const e of engagements) {
      const bucket = e.archived ? completed : active;
      bucket.count += 1;
      bucket.fees += parseFloat(e.fee_amount) || 0;
      bucket.hours += parseFloat(e.total_hours) || 0;
      bucket.pending += parseInt(e.pending_items_count) || 0;
    }
    return { activeTotals: active, completedTotals: completed };
  }, [engagements]);

  const viewCounts = useMemo(
    () => ({
      active: engagements.filter((e) => !e.archived).length,
      completed: engagements.filter((e) => !!e.archived).length,
      all: engagements.length,
    }),
    [engagements],
  );

  const visibleEngagements = useMemo(() => {
    const filtered = engagements.filter((e) => {
      if (filterView === "all") return true;
      if (filterView === "active") return !e.archived;
      if (filterView === "completed") return !!e.archived;
      return true;
    });
    const NUMERIC_FIELDS = new Set([
      "tax_year",
      "fee_amount",
      "total_hours",
      "percent_complete",
      "pending_items_count",
    ]);
    const DATE_FIELDS = new Set(["updated_at"]);
    const dir = sortDirection === "asc" ? 1 : -1;
    const cmp = (a, b) => {
      let av = a[sortField];
      let bv = b[sortField];
      if (NUMERIC_FIELDS.has(sortField)) {
        const an = Number.isFinite(parseFloat(av)) ? parseFloat(av) : -Infinity;
        const bn = Number.isFinite(parseFloat(bv)) ? parseFloat(bv) : -Infinity;
        return (an - bn) * dir;
      }
      if (DATE_FIELDS.has(sortField)) {
        const at = av ? new Date(av).getTime() : 0;
        const bt = bv ? new Date(bv).getTime() : 0;
        return (at - bt) * dir;
      }
      const as = (av ?? "").toString().toLowerCase();
      const bs = (bv ?? "").toString().toLowerCase();
      return as.localeCompare(bs) * dir;
    };
    return [...filtered].sort(cmp);
  }, [engagements, filterView, sortField, sortDirection]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      // Numbers/dates default to descending (most recent / largest first)
      const numericOrDate = [
        "tax_year",
        "fee_amount",
        "total_hours",
        "percent_complete",
        "pending_items_count",
        "updated_at",
      ].includes(field);
      setSortDirection(numericOrDate ? "desc" : "asc");
    }
  };

  // Close any open row menu when clicking outside
  useEffect(() => {
    if (!openMenuId) return;
    const handler = (ev) => {
      if (ev.target.closest("[data-row-menu]")) return;
      setOpenMenuId(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openMenuId]);

  const setArchived = async (id, archived) => {
    setOpenMenuId(null);
    setEngagements((prev) =>
      prev.map((e) => (e.id === id ? { ...e, archived } : e)),
    );
    const { error } = await supabase
      .from("engagements")
      .update({ archived })
      .eq("id", id);
    if (error) {
      setError(error.message);
      refresh();
    }
  };

  const deleteEngagement = async (id, name) => {
    setOpenMenuId(null);
    if (
      !confirm(
        `Delete "${name || "this engagement"}"?\n\nThis permanently removes the engagement and all its data. This cannot be undone.`,
      )
    )
      return;
    setEngagements((prev) => prev.filter((e) => e.id !== id));
    const { error } = await supabase.from("engagements").delete().eq("id", id);
    if (error) {
      setError(error.message);
      refresh();
    }
  };

  const createEngagement = async () => {
    setCreating(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from("engagements")
        .insert({
          name: "Untitled engagement",
          status: "not_started",
          fee_type: "fixed",
          created_by: user?.id || null,
          last_edited_by: user?.id || null,
          assigned_to_id: user?.id || null,
        })
        .select("id")
        .single();
      if (error) throw error;
      onOpenEngagement(data.id);
    } catch (e) {
      setError(e.message || String(e));
      setCreating(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-[#F1F7FC] sans">
      {/* Top bar */}
      <header className="bg-white/80 backdrop-blur border-b border-sky-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/linked_accounting_logo.png"
              alt="Linked Accounting LLP"
              className="h-9 w-auto"
            />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500">
                Linked Accounting LLP
              </div>
              <div className="text-sm font-semibold text-stone-900">
                R&amp;D Tax Credit Workbench
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <button
              onClick={() => setFeedbackOpen(true)}
              className="relative px-3 py-1.5 text-xs rounded-md border border-stone-300 hover:bg-stone-50 text-stone-700 flex items-center gap-1.5"
              title="View firm-wide feedback pool"
            >
              <MessageSquare size={13} />
              Feedback
              {feedbackCounts.open > 0 && (
                <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold mono">
                  {feedbackCounts.open}
                </span>
              )}
            </button>
            <div className="text-right hidden sm:block">
              <div className="font-medium text-stone-900">
                {profile?.display_name || profile?.email}
              </div>
              <div className="text-xs text-stone-500 mono">
                {profile?.role}
              </div>
            </div>
            <button
              onClick={signOut}
              className="px-3 py-1.5 text-xs rounded-md border border-stone-300 hover:bg-stone-50 text-stone-700"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Hero band: vivid sky → cyan gradient + decorative blurred blobs */}
      <section className="relative overflow-hidden bg-gradient-to-br from-sky-200 via-sky-100 to-transparent border-b border-sky-200">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 -right-32 w-[520px] h-[520px] rounded-full bg-sky-400/35 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-24 left-1/3 w-[400px] h-[400px] rounded-full bg-cyan-300/40 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-20 left-10 w-[260px] h-[260px] rounded-full bg-sky-500/15 blur-3xl"
        />
        <div className="relative max-w-7xl mx-auto px-6 pt-10 pb-12">
          <div className="flex items-end justify-between gap-6 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-sky-900 border border-sky-900 text-[11px] uppercase tracking-widest text-sky-300 mono shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />
                Firm Workbench
              </div>
              <h1 className="serif text-5xl text-stone-900 leading-tight mb-2">
                R&amp;D Studies
              </h1>
              <p className="text-sm text-stone-700 leading-relaxed max-w-2xl">
                A workbench for firms running IRC §41 research credit studies.
                Built to scale with your engagement load — every figure on the
                schedule traces back to the documentation that supports it.
              </p>
            </div>
            <button
              onClick={createEngagement}
              disabled={creating}
              className="shrink-0 px-5 py-3 rounded-lg bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed shadow-md shadow-sky-600/20 flex items-center gap-2 transition"
            >
              <Plus size={16} strokeWidth={2.5} />
              {creating ? "Creating…" : "New R&D study"}
            </button>
          </div>

          {/* Stats — Active vs Completed, side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <StatGroup
              title="Active studies"
              icon={Briefcase}
              accent="sky"
              metrics={[
                { label: "Studies", value: activeTotals.count, mono: false },
                { label: "Total fees", value: fmtMoney(activeTotals.fees), mono: true },
                { label: "Hours", value: (activeTotals.hours || 0).toFixed(1), mono: true },
                { label: "Pending", value: activeTotals.pending, mono: false, alert: activeTotals.pending > 0 },
              ]}
            />
            <StatGroup
              title="Completed studies"
              icon={CheckCircle2}
              accent="emerald"
              metrics={[
                { label: "Studies", value: completedTotals.count, mono: false },
                { label: "Total fees", value: fmtMoney(completedTotals.fees), mono: true },
                { label: "Hours", value: (completedTotals.hours || 0).toFixed(1), mono: true },
                { label: "Pending", value: completedTotals.pending, mono: false },
              ]}
            />
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Error */}
        {error && (
          <div className="mb-6 px-4 py-3 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">
            {error}
          </div>
        )}

        {/* Section heading + view filter */}
        {!loading && engagements.length > 0 && (
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <h2 className="serif text-xl text-stone-900">Engagements</h2>
              <p className="text-xs text-stone-500 mt-0.5">
                Click any row to open the workbench. Use the menu (⋯) to mark
                complete or delete.
              </p>
            </div>
            <div className="inline-flex items-center rounded-lg bg-stone-100 p-1 text-xs sans">
              {[
                { id: "active",    label: "Active",    count: viewCounts.active },
                { id: "completed", label: "Completed", count: viewCounts.completed },
                { id: "all",       label: "All",       count: viewCounts.all },
              ].map((seg) => (
                <button
                  key={seg.id}
                  onClick={() => setFilterView(seg.id)}
                  className={`px-3 py-1.5 rounded-md font-medium transition ${
                    filterView === seg.id
                      ? "bg-white text-stone-900 shadow-sm"
                      : "text-stone-600 hover:text-stone-900"
                  }`}
                >
                  {seg.label}
                  <span className="ml-1.5 mono text-[11px] text-stone-500">
                    {seg.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="text-stone-500 text-sm">Loading engagements…</div>
        ) : engagements.length === 0 ? (
          <EmptyState onCreate={createEngagement} creating={creating} />
        ) : visibleEngagements.length === 0 ? (
          <FilteredEmptyState
            view={filterView}
            onShowAll={() => setFilterView("all")}
          />
        ) : (
          <div className="bg-white border border-stone-200 rounded-xl shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-wider text-stone-500">
                  <SortHeader label="Engagement" field="name"             sortField={sortField} sortDirection={sortDirection} onSort={toggleSort} align="left" />
                  <SortHeader label="Tax year"   field="tax_year"          sortField={sortField} sortDirection={sortDirection} onSort={toggleSort} align="left" />
                  <SortHeader label="Status"     field="status"            sortField={sortField} sortDirection={sortDirection} onSort={toggleSort} align="left" />
                  <th className="text-left px-4 py-3 font-semibold">Assigned</th>
                  <th className="text-left px-4 py-3 font-semibold">Fee type</th>
                  <SortHeader label="Fee"        field="fee_amount"        sortField={sortField} sortDirection={sortDirection} onSort={toggleSort} align="right" />
                  <SortHeader label="% Complete" field="percent_complete"  sortField={sortField} sortDirection={sortDirection} onSort={toggleSort} align="left" widthClass="w-40" />
                  <SortHeader label="Pending"    field="pending_items_count" sortField={sortField} sortDirection={sortDirection} onSort={toggleSort} align="center" />
                  <SortHeader label="Last edited" field="updated_at"       sortField={sortField} sortDirection={sortDirection} onSort={toggleSort} align="right" />
                  <th className="w-10 px-2 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {visibleEngagements.map((e, idx) => {
                  const assignee = profiles[e.assigned_to_id];
                  const pct = parseFloat(e.percent_complete) || 0;
                  const pending = parseInt(e.pending_items_count) || 0;
                  const isArchived = !!e.archived;
                  // Open the action menu UPWARD for rows near the bottom of the
                  // list so it doesn't get clipped by the page edge.
                  const openUpward =
                    visibleEngagements.length > 2 &&
                    idx >= visibleEngagements.length - 2;
                  return (
                    <tr
                      key={e.id}
                      onClick={() => onOpenEngagement(e.id)}
                      className={`border-b border-stone-100 last:border-b-0 hover:bg-sky-50/40 cursor-pointer transition ${
                        isArchived ? "bg-stone-50/40 text-stone-500" : ""
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`font-medium ${isArchived ? "text-stone-500" : "text-stone-900"}`}>
                            {e.name || "Untitled engagement"}
                          </div>
                          {isArchived && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-stone-200 text-stone-600 sans">
                              <Archive size={9} strokeWidth={2.5} />
                              Completed
                            </span>
                          )}
                        </div>
                        {e.client_name && (
                          <div className="text-xs text-stone-500">
                            {e.client_name}
                            {e.entity_type ? ` · ${e.entity_type}` : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 mono text-stone-700">
                        {e.tax_year || "—"}
                      </td>
                      <td className="px-4 py-3">{statusBadge(e.status)}</td>
                      <td className="px-4 py-3 text-stone-700">
                        {assignee ? (
                          <span title={assignee.email}>
                            {assignee.display_name || assignee.email}
                          </span>
                        ) : (
                          <span className="text-stone-400">Unassigned</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-stone-700">
                        {FEE_TYPE_LABELS[e.fee_type] || e.fee_type || "—"}
                      </td>
                      <td className="px-4 py-3 text-right mono text-stone-900">
                        {fmtMoney(e.fee_amount)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                            <div
                              className="h-full bg-sky-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="mono text-xs text-stone-600 w-9 text-right">
                            {pct.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {pending > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-amber-50 text-amber-700 mono">
                            {pending}
                          </span>
                        ) : (
                          <span className="text-stone-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-stone-500 text-xs mono">
                        {fmtRelative(e.updated_at)}
                      </td>
                      <td
                        className="px-2 py-3 text-right relative"
                        data-row-menu
                        onClick={(ev) => ev.stopPropagation()}
                      >
                        <button
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setOpenMenuId(openMenuId === e.id ? null : e.id);
                          }}
                          className="w-7 h-7 rounded-md hover:bg-stone-200 text-stone-500 hover:text-stone-800 inline-flex items-center justify-center"
                          title="Actions"
                          aria-label="Engagement actions"
                        >
                          <MoreVertical size={15} />
                        </button>
                        {openMenuId === e.id && (
                          <div
                            className={`absolute right-2 z-50 min-w-[180px] bg-white border border-stone-200 rounded-lg shadow-xl overflow-hidden text-left sans ${
                              openUpward ? "bottom-10 mb-1" : "top-10"
                            }`}
                          >
                            {!isArchived ? (
                              <button
                                onClick={() => setArchived(e.id, true)}
                                className="w-full px-3 py-2 text-xs text-stone-700 hover:bg-stone-50 flex items-center gap-2"
                              >
                                <Archive size={13} />
                                Mark complete
                              </button>
                            ) : (
                              <button
                                onClick={() => setArchived(e.id, false)}
                                className="w-full px-3 py-2 text-xs text-stone-700 hover:bg-stone-50 flex items-center gap-2"
                              >
                                <ArchiveRestore size={13} />
                                Restore to active
                              </button>
                            )}
                            <div className="h-px bg-stone-100" />
                            <button
                              onClick={() => deleteEngagement(e.id, e.name)}
                              className="w-full px-3 py-2 text-xs text-rose-700 hover:bg-rose-50 flex items-center gap-2"
                            >
                              <Trash2 size={13} />
                              Delete permanently
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {feedbackOpen && (
        <FeedbackPanel profile={profile} onClose={() => setFeedbackOpen(false)} />
      )}
    </div>
  );
}

const ACCENT_PALETTE = {
  sky:     { bar: "bg-gradient-to-r from-sky-400 to-cyan-500",     iconBg: "bg-sky-100",     iconColor: "text-sky-700",     valueColor: "text-stone-900" },
  emerald: { bar: "bg-gradient-to-r from-emerald-400 to-teal-500", iconBg: "bg-emerald-100", iconColor: "text-emerald-700", valueColor: "text-stone-900" },
  violet:  { bar: "bg-gradient-to-r from-violet-400 to-fuchsia-500",iconBg: "bg-violet-100", iconColor: "text-violet-700",  valueColor: "text-stone-900" },
  amber:   { bar: "bg-gradient-to-r from-amber-400 to-orange-500", iconBg: "bg-amber-100",   iconColor: "text-amber-700",   valueColor: "text-amber-700" },
  teal:    { bar: "bg-gradient-to-r from-teal-400 to-cyan-500",    iconBg: "bg-teal-100",    iconColor: "text-teal-700",    valueColor: "text-stone-900" },
  stone:   { bar: "bg-stone-300",                                  iconBg: "bg-stone-100",   iconColor: "text-stone-500",   valueColor: "text-stone-900" },
};

function StatGroup({ title, icon: Icon, accent = "sky", metrics }) {
  const palette = ACCENT_PALETTE[accent] || ACCENT_PALETTE.sky;
  return (
    <div className="relative bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition">
      <div className={`absolute top-0 left-0 right-0 h-1.5 ${palette.bar}`} />
      <div className="px-5 pt-5 pb-4 flex items-center justify-between border-b border-stone-100">
        <div className="flex items-center gap-2.5">
          {Icon && (
            <div className={`w-8 h-8 rounded-lg ${palette.iconBg} ${palette.iconColor} flex items-center justify-center shrink-0`}>
              <Icon size={15} strokeWidth={2} />
            </div>
          )}
          <h3 className="serif text-base text-stone-900 leading-tight">
            {title}
          </h3>
        </div>
      </div>
      <div className="grid grid-cols-4 divide-x divide-stone-100">
        {metrics.map((m) => (
          <div key={m.label} className="px-3 py-3 text-center">
            <div className="text-[9px] uppercase tracking-widest text-stone-500 font-semibold mb-1">
              {m.label}
            </div>
            <div
              className={`${m.mono ? "mono" : "serif"} text-xl num-tabular leading-tight ${
                m.alert ? "text-amber-700" : "text-stone-900"
              }`}
            >
              {m.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SortHeader({
  label,
  field,
  sortField,
  sortDirection,
  onSort,
  align = "left",
  widthClass = "",
}) {
  const isActive = sortField === field;
  const justify =
    align === "right"
      ? "justify-end"
      : align === "center"
        ? "justify-center"
        : "justify-start";
  return (
    <th
      onClick={() => onSort(field)}
      className={`px-4 py-3 font-semibold cursor-pointer select-none transition hover:bg-stone-100 ${widthClass} ${
        align === "right"
          ? "text-right"
          : align === "center"
            ? "text-center"
            : "text-left"
      } ${isActive ? "text-stone-900" : ""}`}
      title={`Sort by ${label.toLowerCase()}`}
    >
      <span className={`inline-flex items-center gap-1 ${justify}`}>
        <span>{label}</span>
        {isActive ? (
          sortDirection === "asc" ? (
            <ArrowUp size={11} strokeWidth={2.5} className="text-sky-600" />
          ) : (
            <ArrowDown size={11} strokeWidth={2.5} className="text-sky-600" />
          )
        ) : (
          <ArrowUpDown size={10} strokeWidth={2} className="text-stone-300" />
        )}
      </span>
    </th>
  );
}

function FilteredEmptyState({ view, onShowAll }) {
  const messages = {
    active: {
      title: "No active studies",
      body: "Every engagement is marked complete. Toggle the view to see them.",
    },
    completed: {
      title: "Nothing in the archive yet",
      body: "Engagements you mark complete will land here.",
    },
    all: {
      title: "No engagements found",
      body: "Try a different view.",
    },
  };
  const m = messages[view] || messages.all;
  return (
    <div className="bg-white border border-stone-200 rounded-xl px-8 py-12 text-center">
      <div className="serif text-lg text-stone-900 mb-2">{m.title}</div>
      <p className="text-sm text-stone-600 mb-4">{m.body}</p>
      {view !== "all" && (
        <button
          onClick={onShowAll}
          className="text-xs text-sky-700 hover:text-sky-900 font-medium"
        >
          Show all engagements →
        </button>
      )}
    </div>
  );
}

function EmptyState({ onCreate, creating }) {
  return (
    <div className="bg-white border border-dashed border-sky-300 rounded-xl px-8 py-16 text-center">
      <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-sky-100 text-sky-600 flex items-center justify-center">
        <Briefcase size={26} strokeWidth={1.75} />
      </div>
      <div className="serif text-2xl text-stone-900 mb-2">
        No engagements yet
      </div>
      <p className="text-sm text-stone-600 mb-6 max-w-sm mx-auto">
        Start your first R&amp;D study. You can rename it and fill in client
        details inside the workbench.
      </p>
      <button
        onClick={onCreate}
        disabled={creating}
        className="px-4 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium inline-flex items-center gap-2 shadow-md shadow-sky-600/20"
      >
        <Plus size={14} strokeWidth={2.5} />
        {creating ? "Creating…" : "New R&D study"}
      </button>
    </div>
  );
}
